if (!customElements.get('mobile-sticky-atc')) {
  customElements.define(
    'mobile-sticky-atc',
    class MobileStickyAtc extends HTMLElement {
      constructor() {
        super();
        this.mediaQuery = window.matchMedia('(max-width: 768px)');
        this.rafId = null;
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.priceOutput = this.querySelector('.mobile-sticky-atc__price');
        this.proxyButton = this.querySelector('.mobile-sticky-atc__button');
        this.originalProductForm = this.productInfo?.querySelector('product-form');
        this.originalFormElement = this.originalProductForm?.querySelector('form[data-type="add-to-cart-form"]');
        this.originalButton = this.originalProductForm?.querySelector('[type="submit"][name="add"]');
        this.variantInput = this.originalFormElement?.querySelector('input[name="id"]');
        this.priceSource = this.productInfo?.querySelector(`#price-${this.dataset.sectionId}`);

        if (!this.originalProductForm || !this.originalButton || !this.variantInput || !this.priceSource) return;

        this.onViewportChange = this.queueUpdate.bind(this);
        this.onProductChange = this.queueSync.bind(this);
        this.onProxyClick = this.handleProxyClick.bind(this);

        this.proxyButton.addEventListener('click', this.onProxyClick);
        this.productInfo.addEventListener('change', this.onProductChange);
        window.addEventListener('scroll', this.onViewportChange, { passive: true });
        window.addEventListener('resize', this.onViewportChange, { passive: true });
        this.mediaQuery.addEventListener('change', this.onViewportChange);

        this.stateObserver = new MutationObserver(this.onProductChange);
        this.stateObserver.observe(this.priceSource, { childList: true, subtree: true });
        this.stateObserver.observe(this.originalButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });

        this.widgetObserver = new MutationObserver(this.onViewportChange);
        this.widgetObserver.observe(document.body, { childList: true, subtree: true });

        this.syncState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.proxyButton?.removeEventListener('click', this.onProxyClick);
        this.productInfo?.removeEventListener('change', this.onProductChange);
        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
        this.mediaQuery.removeEventListener('change', this.onViewportChange);
        this.stateObserver?.disconnect();
        this.widgetObserver?.disconnect();
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      queueSync() {
        requestAnimationFrame(() => {
          this.syncState();
          this.updateVisibility();
        });
      }

      queueUpdate() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.updateVisibility();
        });
      }

      syncState() {
        const currentPrice = this.priceSource.querySelector('.price');
        if (currentPrice) this.priceOutput.replaceChildren(currentPrice.cloneNode(true));

        const hasSelectedVariant = this.variantInput.value !== '';
        const originalDisabled = this.originalButton.disabled;
        const submissionLocked = this.originalButton.getAttribute('aria-disabled') === 'true';
        let label = this.dataset.addToCart;

        if (!hasSelectedVariant) {
          label = this.dataset.selectOptions;
        } else if (originalDisabled) {
          label = this.dataset.soldOut;
        }

        this.proxyButton.textContent = label;
        this.proxyButton.disabled = hasSelectedVariant && (originalDisabled || submissionLocked);
        this.proxyButton.setAttribute('aria-disabled', String(this.proxyButton.disabled));
      }

      updateVisibility() {
        if (!this.mediaQuery.matches || !this.originalProductForm?.isConnected) {
          this.setVisible(false);
          return;
        }

        const originalBounds = this.originalProductForm.getBoundingClientRect();
        const originalHasScrolledAboveViewport = originalBounds.bottom <= 0;
        this.setVisible(originalHasScrolledAboveViewport);
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.updateWhatsAppClearance(visible);
      }

      updateWhatsAppClearance(visible) {
        let clearance = 0;
        if (visible) {
          const candidates = document.querySelectorAll(
            'a[href*="wa.me" i], a[href*="whatsapp" i], iframe[title*="whatsapp" i], [aria-label*="whatsapp" i]'
          );
          for (const candidate of candidates) {
            if (this.contains(candidate)) continue;
            const bounds = candidate.getBoundingClientRect();
            const isVisible = bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.right > 0;
            const overlapsBar = bounds.bottom > window.innerHeight - this.offsetHeight;
            if (isVisible && overlapsBar && bounds.left > window.innerWidth / 2) {
              clearance = Math.max(clearance, Math.min(96, window.innerWidth - bounds.left + 8));
            }
          }
        }
        this.style.setProperty('--mobile-sticky-atc-right-clearance', `${clearance}px`);
      }

      handleProxyClick() {
        if (!this.variantInput.value) {
          const variantSelector = this.productInfo.querySelector('variant-selects');
          variantSelector?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          variantSelector?.querySelector('select, input:not([disabled])')?.focus({ preventScroll: true });
          return;
        }
        if (this.originalButton.disabled || this.originalButton.getAttribute('aria-disabled') === 'true') return;
        this.originalButton.click();
      }
    }
  );
}
